/** A table's filters, mirrored in the address bar - so a filtered list is a link somebody can send and
 * a place Back returns to (EXP-12's reason, for the table pages).
 *
 * A filter `site` with the values `a` and `b` is `?f_site=a,b`; the table's own search is `?fq=text`.
 * Each value is URL-encoded before the commas join them, so a value that contains one survives. The
 * keys are **screen-owned** (`search.ts`): they do not follow the reader to another screen, where
 * they would narrow a list that shows no chips for them.
 *
 * The URL is the state - nothing is held beside it - so the browser's Back and a pasted link agree
 * with the chips by construction. A write replaces the history entry rather than adding one: a chip
 * per keystroke of Back presses is not a history.
 */

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import type { FilterState } from "../filters";
import { FILTER_PREFIX, SEARCH_KEY, isTableKey } from "./urlKeys";

/** A search value the router parsed (string, number, boolean or array) as the string it was written as. */
function asText(value: unknown): string {
  if (Array.isArray(value)) return value.map(asText).join(",");
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** The filter state a search object holds. Pure, so the round trip has a test. */
export function filtersFromSearch(search: Readonly<Record<string, unknown>>): FilterState {
  const values: Record<string, readonly string[]> = {};
  for (const [key, raw] of Object.entries(search)) {
    if (!key.startsWith(FILTER_PREFIX) || key.length === FILTER_PREFIX.length) continue;
    const list = asText(raw)
      .split(",")
      .filter((part) => part !== "")
      .map(decode);
    if (list.length > 0) values[key.slice(FILTER_PREFIX.length)] = list;
  }
  return { search: asText(search[SEARCH_KEY]), values };
}

/** `prev` with the filter keys replaced by `state`'s. */
export function searchWithFilters(
  prev: Readonly<Record<string, unknown>>,
  state: FilterState,
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(prev)) {
    if (!isTableKey(key)) next[key] = value;
  }
  for (const [id, list] of Object.entries(state.values)) {
    if (list.length > 0) next[`${FILTER_PREFIX}${id}`] = list.map(encodeURIComponent).join(",");
  }
  if (state.search.trim() !== "") next[SEARCH_KEY] = state.search;
  return next;
}

export function useUrlFilters(): readonly [FilterState, (next: FilterState) => void] {
  const navigate = useNavigate();
  const search: Record<string, unknown> = useSearch({ strict: false });
  const signature = Object.entries(search)
    .filter(([key]) => isTableKey(key))
    .map(([key, value]) => `${key}=${asText(value)}`)
    .sort()
    .join("&");
  // The signature is the dependency: `search` is a new object on every location change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const state = useMemo(() => filtersFromSearch(search), [signature]);
  const set = useCallback(
    (next: FilterState) => {
      void navigate({
        to: ".",
        replace: true,
        search: (prev: Record<string, unknown>) => searchWithFilters(prev, next),
      });
    },
    [navigate],
  );
  return [state, set] as const;
}
